import React from 'react';
import { Outlet, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const MainLayout: React.FC = () => {
  const { isAuthenticated, logout } = useAuth();

  return (
    <div className="min-h-screen bg-surface">
      {/* Navigation */}
      <nav className="bg-surface border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <span className="text-2xl font-bold gradient-text">Clevor</span>
              </Link>
              <div className="hidden md:flex ml-10 space-x-8">
                <Link to="/features" className="text-content-muted hover:text-content px-3 py-2 text-sm font-medium">
                  Features
                </Link>
                <Link to="/pricing" className="text-content-muted hover:text-content px-3 py-2 text-sm font-medium">
                  Pricing
                </Link>
                <Link to="/about" className="text-content-muted hover:text-content px-3 py-2 text-sm font-medium">
                  About
                </Link>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <>
                  <Link to="/dashboard" className="btn-primary">
                    Dashboard
                  </Link>
                  <button onClick={() => logout()} className="text-content-muted hover:text-content">
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-content-muted hover:text-content px-3 py-2 text-sm font-medium">
                    Sign in
                  </Link>
                  <Link to="/signup" className="btn-primary">
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">Clevor</h3>
              <p className="text-gray-400 text-sm">
                The execution layer for government contracting — monitor opportunities, track
                changes, and move from discovery to submission without the manual work.
              </p>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                {/* Footer rather than the top nav on purpose. The page describes
                    something that is not open yet, and the primary nav should
                    only lead to things a visitor can use today. */}
                <li>
                  <Link to="/for-agents" className="hover:text-white">
                    For AI Agents
                    <span className="ml-2 rounded bg-gray-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-300">
                      Soon
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold uppercase tracking-wider mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="mailto:support@clevor.ai" className="hover:text-white">Contact</a></li>
                {/* Legal has to be reachable from any page, not only from the
                    signup checkbox — someone deciding whether to trust the
                    product with their bid data should not have to start
                    creating an account to read the privacy policy. */}
                <li><Link to="/terms" className="hover:text-white">Terms of Service</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-800 text-center text-gray-400 text-sm">
            © 2026 Clevor. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
